import { useState, useEffect, useCallback } from 'react';

/**
 * usePageLoader
 *
 * Shows a full-page overlay spinner the moment a component mounts,
 * then hides it once the component signals it is ready.
 *
 * Usage:
 *   const { pageReady, markPageReady } = usePageLoader();
 *   // Call markPageReady() when your data has loaded and the UI is ready.
 *   // Render <PageLoader /> when !pageReady.
 *
 * The tiny `minMs` delay (default 200 ms) prevents a jarring flash of the
 * overlay on fast connections while still covering slow-network white screens.
 */
function usePageLoader(minMs = 200) {
    const [pageReady, setPageReady] = useState(false);
    const [minPassed, setMinPassed] = useState(false);

    // Start the minimum-display timer immediately on mount
    useEffect(() => {
        const timer = setTimeout(() => setMinPassed(true), minMs);
        return () => clearTimeout(timer);
    }, [minMs]);

    // markPageReady can be called at any time; the overlay only hides
    // once BOTH the min time has passed AND the page signals ready.
    const [dataReady, setDataReady] = useState(false);

    const markPageReady = useCallback(() => setDataReady(true), []);

    useEffect(() => {
        if (dataReady && minPassed) {
            setPageReady(true);
        }
    }, [dataReady, minPassed]);

    return { pageReady, markPageReady };
}

export default usePageLoader;